import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Bell, X, Calendar, CheckSquare, UserCheck } from 'lucide-react'
import { useCalendarEvents } from '@/hooks/useCalendar'
import { useTasks } from '@/hooks/useTasks'
import { useAuthStore } from '@/store/authStore'
import { useSocket } from '@/hooks/useSocket'

type AlertItem = {
  id: string
  kind: 'event' | 'task' | 'task_assigned'
  title: string
  description?: string
  startTime: string
}

const WINDOW_MS = 20 * 60 * 1000 // 20 minutes

function minsLeft(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 60_000)
}

// Stable date window: today 00:00 to tomorrow 23:59 — same key for the whole day
function getTodayWindow() {
  const start = new Date(); start.setHours(0, 0, 0, 0)
  const end = new Date(); end.setDate(end.getDate() + 1); end.setHours(23, 59, 59, 999)
  return { startDate: start.toISOString(), endDate: end.toISOString() }
}

export function ReminderAlert() {
  const { isAuthenticated } = useAuthStore()
  const [alerts, setAlerts] = useState<AlertItem[]>([])
  const shownIds = useRef<Set<string>>(new Set())

  // Stable params — recalculated only once per component mount
  const calendarParams = useMemo(() => ({ ...getTodayWindow(), size: 100 }), [])

  // Fetch calendar events for today + tomorrow
  const { events } = useCalendarEvents(calendarParams)

  // Fetch all active tasks (for due-soon detection)
  const { tasks } = useTasks({ page: 1, size: 100 })

  // Listen for task_assigned socket notifications
  const { on, off } = useSocket()
  const handleSocketNotification = useCallback((data: unknown) => {
    const notif = data as { id?: string; type?: string; title?: string; message?: string; created_at?: string }
    if (notif?.type !== 'task_assigned') return
    const key = `notif-${notif.id ?? Date.now()}`
    if (shownIds.current.has(key)) return
    shownIds.current.add(key)
    setAlerts(prev => [...prev, {
      id: key,
      kind: 'task_assigned',
      title: notif.title ?? 'Task assigned to you',
      description: notif.message,
      startTime: notif.created_at ?? new Date().toISOString(),
    }])
  }, [])

  useEffect(() => {
    on('notification:new', handleSocketNotification)
    return () => off('notification:new', handleSocketNotification)
  }, [on, off, handleSocketNotification])

  useEffect(() => {
    if (!isAuthenticated) return

    const check = () => {
      const nowMs = Date.now()
      const newAlerts: AlertItem[] = []

      // --- Calendar events ---
      for (const ev of events) {
        if (ev.status === 'cancelled') continue
        const key = `ev-${ev.id}`
        if (shownIds.current.has(key)) continue
        const ms = new Date(ev.startTime).getTime() - nowMs
        if (ms >= -2 * 60 * 1000 && ms <= WINDOW_MS) {
          shownIds.current.add(key)
          newAlerts.push({ id: key, kind: 'event', title: ev.title, description: ev.description, startTime: ev.startTime })
        }
      }

      // --- Tasks ---
      for (const t of tasks) {
        if (!t.dueDate || t.status === 'completed' || t.status === 'cancelled') continue
        const key = `task-${t.id}`
        if (shownIds.current.has(key)) continue
        const ms = new Date(t.dueDate).getTime() - nowMs
        if (ms >= 0 && ms <= WINDOW_MS) {
          shownIds.current.add(key)
          newAlerts.push({ id: key, kind: 'task', title: t.title, description: t.description, startTime: t.dueDate })
        }
      }

      if (newAlerts.length > 0) setAlerts(prev => [...prev, ...newAlerts])
    }

    check()
    const timer = setInterval(check, 60_000)
    return () => clearInterval(timer)
  }, [events, tasks, isAuthenticated])

  const dismiss = (id: string) => setAlerts(prev => prev.filter(a => a.id !== id))

  if (!isAuthenticated || alerts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none" style={{ maxWidth: '340px' }}>
      {alerts.map(alert => {
        const mins = minsLeft(alert.startTime)
        const isEvent = alert.kind === 'event'
        const isAssigned = alert.kind === 'task_assigned'
        return (
          <div
            key={alert.id}
            className={`pointer-events-auto bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-4 flex gap-3 items-start animate-slide-in-right border ${isAssigned ? 'border-teal-300 dark:border-teal-500' : 'border-orange-300 dark:border-orange-500'}`}
          >
            {/* Icon */}
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isAssigned ? 'bg-teal-100 dark:bg-teal-900/40' : isEvent ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-orange-100 dark:bg-orange-900/40'}`}>
              {isAssigned
                ? <UserCheck className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                : isEvent
                  ? <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  : <CheckSquare className="w-4 h-4 text-orange-500 dark:text-orange-400" />
              }
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className={`text-[11px] font-semibold uppercase tracking-wide ${isAssigned ? 'text-teal-600 dark:text-teal-400' : isEvent ? 'text-purple-600 dark:text-purple-400' : 'text-orange-600 dark:text-orange-400'}`}>
                {isAssigned ? 'Task Assigned' : isEvent ? 'Upcoming Meeting' : 'Task Due Soon'}
              </p>
              <p className="text-xs font-medium text-navy dark:text-slate-100 truncate mt-0.5">{alert.title}</p>
              {alert.description && (
                <p className="text-[10px] text-steel dark:text-slate-400 truncate mt-0.5">{alert.description}</p>
              )}
              {!isAssigned && (
                <p className={`text-[10px] font-semibold mt-1 ${isEvent ? 'text-purple-500 dark:text-purple-400' : 'text-orange-500 dark:text-orange-400'}`}>
                  {mins <= 0
                    ? (isEvent ? 'Starting now!' : 'Due now!')
                    : isEvent
                      ? `Starting in ${mins} minute${mins !== 1 ? 's' : ''}`
                      : `Due in ${mins} minute${mins !== 1 ? 's' : ''}`}
                </p>
              )}
            </div>

            {/* Dismiss */}
            <button
              onClick={() => dismiss(alert.id)}
              className="flex-shrink-0 p-1 rounded text-steel hover:text-navy dark:hover:text-slate-100 hover:bg-cloud dark:hover:bg-slate-700 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        )
      })}

      {/* Bell badge when there are multiple alerts */}
      {alerts.length > 1 && (
        <div className="pointer-events-auto self-end flex items-center gap-1.5 bg-orange-500 text-white text-xs font-semibold px-2.5 py-1.5 rounded-full shadow-lg">
          <Bell size={12} />
          {alerts.length} reminders
        </div>
      )}
    </div>
  )
}

export default ReminderAlert
